// Copyright 2020 TOKU Systems

import React, { useState, useEffect } from 'react';
import AppBarWithMenu from '../app-bar-with-menu';
import { Container } from '@material-ui/core';
import MaterialTable from 'material-table';
import illumass from '../../services/illumass';
import { Hardpoint, EntityRef } from '@equestria/spike';
import * as _ from 'lodash';
import { List } from 'immutable';
import { useHistory } from 'react-router-dom';
import moment from 'moment-timezone';
import InfoDialog from '../info-dialog';

class HardpointRecord {
  constructor(readonly hardpoint: Hardpoint) {}

  get asset(): string {
    return (this.hardpoint.asset as EntityRef).shortName ?? '???';
  }

  get name(): string {
    return this.hardpoint.name ?? '???';
  }

  get serialNumber(): string {
    const deviceEntry = _.find(
      _.entries(this.hardpoint.schedule ?? {}),
      (pair) => {
        const installInterval = pair[1];
        const now = moment.utc();
        const start =
          installInterval?.interval?.start !== undefined
            ? moment.utc(installInterval.interval.start)
            : undefined;
        const end =
          installInterval?.interval?.end !== undefined
            ? moment.utc(installInterval.interval.end)
            : undefined;

        const onOrAfterStart = start === undefined || !now.isBefore(start);
        const beforeEnd = end === undefined || now.isBefore(end);
        return onOrAfterStart && beforeEnd;
      }
    );

    if (deviceEntry) {
      const installedDevice = deviceEntry[1]?.device as EntityRef;
      return installedDevice?.shortName ?? '???';
    } else {
      return 'None';
    }
  }

  get id(): string {
    return this.hardpoint.id ?? '';
  }

  get idReference(): string {
    return 'hardpoint!' + this.id;
  }

  get slug(): string {
    return this.hardpoint.slug ?? '';
  }

  get slugReference(): string {
    return 'hardpoint.' + this.slug;
  }
}

function Hardpoints(): JSX.Element {
  const [hardpoints, setHardpoints] = useState<List<HardpointRecord>>(List());
  const [loaded, setLoaded] = useState(false);
  const [infoHardpoint, setInfoHardpoint] = useState<
    HardpointRecord | undefined
  >(undefined);
  const history = useHistory();

  useEffect(() => {
    if (!loaded) {
      setLoaded(true);

      illumass.hardpoint.search({}, { size: 10000 }).then((results) => {
        setHardpoints(
          List(results.hits.map((h) => new HardpointRecord(h._source)))
        );
      });
    }
  }, [loaded]);

  const handlePlotClick = (
    _event: unknown,
    target: HardpointRecord | HardpointRecord[]
  ): void => {
    if (_.isArray(target)) {
      // Show plot
      history.push('/plot', {
        originRefs: List<HardpointRecord>(target)
          .map((x) => `hardpoint!${x.hardpoint.id}`)
          .toArray(),
        title: target.map((x) => x.name).join(','),
      });
    } else {
      // Show plot
      history.push('/plot', {
        originRefs: List([`hardpoint!${target.hardpoint.id}`]).toArray(),
        title: target.asset + ', ' + target.name,
      });
    }
  };

  const handleInfoClick = (
    _event: React.MouseEvent<Element, MouseEvent> | undefined,
    row: HardpointRecord | HardpointRecord[]
  ): void => {
    if (_.isArray(row)) {
      setInfoHardpoint(row[0]);
    } else {
      setInfoHardpoint(row);
    }
  };

  const handleInfoClose = (): void => {
    setInfoHardpoint(undefined);
  };

  return (
    <React.Fragment>
      <AppBarWithMenu title="Hardpoints" />
      <Container>
        <MaterialTable
          isLoading={!loaded}
          options={{
            showTitle: false,
            actionsColumnIndex: 3,
          }}
          localization={{
            body: {
              emptyDataSourceMessage: 'No hardpoints',
            },
          }}
          columns={[
            {
              title: 'Asset',
              field: 'asset',
              editable: 'never',
            },
            {
              title: 'Hardpoint',
              field: 'name',
              editable: 'never',
            },
            {
              title: 'Device',
              field: 'serialNumber',
              editable: 'never',
            },
          ]}
          data={hardpoints.toArray()}
          actions={[
            {
              icon: 'show_chart',
              tooltip: 'Plot',
              onClick: handlePlotClick,
            },
            {
              icon: 'info',
              tooltip: 'Info',
              onClick: handleInfoClick,
            },
          ]}
        />
      </Container>
      <InfoDialog
        onClose={handleInfoClose}
        name={infoHardpoint?.name ?? ''}
        idRef={infoHardpoint?.idReference ?? ''}
        slugRef={infoHardpoint?.slugReference ?? ''}
        open={infoHardpoint !== undefined}
      />
    </React.Fragment>
  );
}

export default Hardpoints;
